/**
 * Per-report EDIT capability storage + magic-link demotion.
 *
 * The report bearer token in `?report=<token>` is VIEW-ONLY and safe to copy
 * (see worker migration 0013 + the sharing design). Edit authority rides in a
 * separate `&edit=<secret>` parameter on the owner link the buyer receives
 * (purchase receipt email + post-pay redirect).
 *
 * On first load we capture that secret into localStorage — keyed by the
 * report token, per the per-purchase isolation rule (gotchas #11) — and
 * immediately strip `edit` from the address bar. From then on the visible URL
 * is the safe view link: if the owner copies it and sends it to someone, the
 * recipient gets no edit access. The owner keeps editing because the secret
 * lives in their browser (or, when signed in, via their account session,
 * which the worker also accepts).
 */

const STORAGE_PREFIX = "parcel-report-edit-secret:report:";
const EDIT_PARAM = "edit";
const REPORT_PARAM = "report";

function storageKey(reportToken: string): string {
  return `${STORAGE_PREFIX}${reportToken}`;
}

/** Read the stored edit secret for a report token, or null. */
export function getStoredEditSecret(reportToken: string | null | undefined): string | null {
  if (!reportToken) return null;
  if (typeof localStorage === "undefined") return null;
  try {
    const v = localStorage.getItem(storageKey(reportToken));
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

/** Persist an edit secret for a report token. */
function storeEditSecret(reportToken: string, secret: string): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(storageKey(reportToken), secret);
  } catch (e) {
    console.warn("[report-edit] failed to persist edit secret:", e);
  }
}

/** True when this browser holds an edit secret for the report token. */
export function hasStoredEditSecret(reportToken: string | null | undefined): boolean {
  return getStoredEditSecret(reportToken) != null;
}

/**
 * Magic-link demotion. If the current URL carries `?edit=<secret>` alongside
 * `?report=<token>`, capture the secret into localStorage and rewrite the
 * address bar to drop `edit`, leaving the safe view URL. Idempotent and
 * silent when no `edit` param is present.
 *
 * Call once, early in viewer boot, before anything reads/copies the URL.
 */
export function captureEditSecretFromLocation(): void {
  if (typeof window === "undefined") return;
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }

  const editSecret = readEditParam(url);
  if (!editSecret) return;

  const reportToken = (url.searchParams.get(REPORT_PARAM) ?? "").trim();
  if (reportToken.length > 0) {
    storeEditSecret(reportToken, editSecret);
  }

  // Strip the secret from both the query and any hash form, then rewrite the
  // address bar in place (no navigation / history entry).
  stripEditParam(url);
  try {
    window.history.replaceState(
      window.history.state,
      "",
      url.pathname + url.search + url.hash
    );
  } catch (e) {
    console.warn("[report-edit] failed to strip edit param from URL:", e);
  }
}

/**
 * Capture an edit secret out of an owner URL returned by a mint/fulfill API
 * response (e.g. `.../viewer?report=<token>&edit=<secret>`), storing it under
 * the report token. Used by the in-app ungate-on-payment flow, which acquires
 * the token without a full page reload — so the boot-time
 * `captureEditSecretFromLocation` never runs and the just-bought owner would
 * otherwise land read-only. Silent on a URL with no `edit`.
 */
export function captureEditSecretFromOwnerUrl(
  ownerUrl: string | null | undefined
): void {
  if (!ownerUrl) return;
  let url: URL;
  try {
    url = new URL(ownerUrl, window.location.origin);
  } catch {
    return;
  }
  const secret = (url.searchParams.get(EDIT_PARAM) ?? "").trim();
  const token = (url.searchParams.get(REPORT_PARAM) ?? "").trim();
  if (secret.length > 0 && token.length > 0) {
    storeEditSecret(token, secret);
  }
}

function readEditParam(url: URL): string | null {
  const fromQuery = url.searchParams.get(EDIT_PARAM);
  if (fromQuery && fromQuery.trim().length > 0) return fromQuery.trim();
  // Tolerate a hash-carried form (?#edit=... or #...&edit=...) for parity
  // with how long share payloads can land in the hash.
  if (url.hash.length > 1) {
    const hp = new URLSearchParams(url.hash.replace(/^#/, ""));
    const v = hp.get(EDIT_PARAM);
    if (v && v.trim().length > 0) return v.trim();
  }
  return null;
}

function stripEditParam(url: URL): void {
  url.searchParams.delete(EDIT_PARAM);
  if (url.hash.length > 1) {
    const hp = new URLSearchParams(url.hash.replace(/^#/, ""));
    if (hp.has(EDIT_PARAM)) {
      hp.delete(EDIT_PARAM);
      const rest = hp.toString();
      url.hash = rest.length > 0 ? `#${rest}` : "";
    }
  }
}
